<template>
  <div class="cards">
    <button
      v-for="card in cards"
      :key="card.value"
      type="button"
      class="card"
      :class="{ active: modelValue === card.value }"
      :aria-pressed="modelValue === card.value"
      @click="onPick(card.value)"
    >
      <span class="lbl">{{ card.label }}</span>
      <span class="num">{{ card.count }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

/**
 * The pending-by-type cards, which double as the type filter — clicking a card
 * sets the filter, and clicking the active card clears it back to Total. One
 * control per concept, so the counts can never contradict the visible list.
 *
 * Counts come from /reachouts/counts, which applies every other active filter,
 * so these read as "how many of each type under the current filters".
 */

const TYPES = [
  { value: 'reachout_email', label: 'Email' },
  { value: 'reachout_voicemail', label: 'Voicemail' },
  { value: 'reachout_linkedin_connect', label: 'LinkedIn' },
]

const props = defineProps<{
  modelValue: string
  counts: Record<string, number>
  total: number
}>()

const emit = defineEmits<{ 'update:modelValue': [string] }>()

const cards = computed(() => [
  { value: '', label: 'Total', count: props.total },
  ...TYPES.map((t) => ({ ...t, count: props.counts[t.value] ?? 0 })),
])

function onPick(value: string) {
  emit('update:modelValue', value === props.modelValue ? '' : value)
}
</script>

<style scoped>
/* Tabs, not cards: these share a row with the filter control, so they're sized
   to the text rather than stretched across the width. The chrome (border,
   surface, shadow) is gone — an underline carries the active state, which is
   what a row of type filters reads as. */
.cards {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  min-width: 0;
  flex-wrap: wrap;
}
.card {
  display: inline-flex;
  align-items: baseline;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-2);
  border: none;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  background: none;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
}
.card:hover .lbl { color: var(--color-text-primary); }
.card.active { border-bottom-color: var(--color-accent); }
.lbl {
  font-size: var(--fs-base);
  font-weight: var(--fw-medium);
  color: var(--color-text-tertiary);
}
.card.active .lbl { color: var(--color-text-primary); font-weight: var(--fw-semibold); }
/* Count stays a step down from the label — it's supporting detail, the way
   Gmail's "Important 13" reads. */
.num {
  font-size: var(--fs-sm);
  color: var(--color-text-tertiary);
  font-variant-numeric: tabular-nums;
}
.card.active .num { color: var(--color-accent); }
</style>
